/**
 * Analytics — designer side. Sends two anonymous pings per product per tab session:
 * "studio opened" once the designer is ready, and "item added" on the first new layer.
 * Add-to-cart and orders are counted on the server.
 */
import { track } from './api';

type DesignerHooks = {
  on?: (event: string, handler: (payload: unknown) => void) => void;
  off?: (event: string, handler: (payload: unknown) => void) => void;
  whenReady?: (surface: string, handler: (payload: unknown) => void) => () => void;
};

let started = false;

function once(key: string): boolean {
  try {
    const storageKey = `veyop_analytics:${key}`;
    if (sessionStorage.getItem(storageKey)) return false;
    sessionStorage.setItem(storageKey, '1');
  } catch {
    // Private mode: count the event; worst case a reload counts it again.
  }
  return true;
}

function send(event: 'studio_open' | 'item_added', productId: number): void {
  if (!productId || !once(`${event}:${productId}`)) return;
  void track(event, productId).catch(() => undefined);
}

function start(): void {
  const hooks = (window as unknown as { Veyop?: { hooks?: DesignerHooks } }).Veyop?.hooks;
  if (started || !hooks?.whenReady || !hooks.on) return;
  started = true;

  hooks.whenReady('designer', (payload) => {
    const ready = (payload ?? {}) as { productId?: unknown; config?: { mode?: string } };
    // The admin template editor reuses the designer; it is not a shopper session.
    if (ready.config?.mode === 'template-admin') return;
    const productId = Number(ready.productId) || 0;
    send('studio_open', productId);
    // Layers restored from a cart or draft load before "ready", so only later additions count.
    const onAdd = () => {
      send('item_added', productId);
      hooks.off?.('add.item.design', onAdd);
    };
    hooks.on?.('add.item.design', onAdd);
  });
}

function boot(): void {
  start();
  const begin = Date.now();
  const tick = () => {
    start();
    if (started || Date.now() - begin > 10000) return;
    setTimeout(tick, 50);
  };
  tick();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
